import ndjsonStream from "can-ndjson-stream";
import zodToJsonSchema from 'zod-to-json-schema';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { chat_config_key, ChatConfig, get_config } from './management';
import { ChatContext, ChatItem } from "./ChatContext";
//import { countries_filter, free_text_filter } from "../filter/utils";


const IpSearchArgs = z.object({
  search_term: z.string(),
})

//const IpFilterArgs = z.discriminatedUnion('name', [
//  z.object({
//    name: z.literal(countries_filter),
//    config: z.array(z.string()).describe("A list of ISO-3166-1 alpha-2 country codes (e.g. ['DE', 'US'])"),
//  }),
//  z.object({
//    name: z.literal(free_text_filter),
//    config: z.string().describe("A free text search term. Only IP rights with this term are shown."),
//  }),
//  z.object({
//    name: z.literal(tag_filter),
//    config: z.string().describe("A list of tags. Only IP rights with at least one of the tags are shown."),
//  }),
//]).describe("A filter to apply to the IP portfolio to only see a subset.")

// TODO: each filter as a separate tool

const tools = [
  { name: 'answer', description: 'Generate an answer directly from the complete history of messages.' },
  {
    name: 'ip_search',
    description:
      'Search for an IP right (patent, trade mark) with a search term. Use if you need to know something about a concrete IP right. The search term should preferably be a single word.',
    parameters: zodToJsonSchema(IpSearchArgs),
  },
  { name: 'summarize', description: 'Summarize the current IP right (patent, trade mark) in view.' },
   // THIS DOESN'T WORK
  //{
   // name: 'filter', 
   // description: 'Filter the IP portfolio to only show a subset of IP rights.',
   // parameters: zodToJsonSchema(IpFilterArgs),
   //},
   // THIS DOESN'T WORK
   //{
   // name: free_text_filter,
   // description: 'A filter for any search term. Only IP rights with this term are shown.',
   // parameters: zodToJsonSchema(z.string()),
   //},
   //{
   // name: countries_filter,
   // description: 'A filter for a list of countries.',
   // parameters: zodToJsonSchema(z.array(z.string()).describe("Two-letter country codes like ['DE', 'US']")),
   //},
].map(tool => ({ type: 'function', function: tool }))

//console.log({tools})

export function ChatProvider({ children }: { children: React.ReactNode }) {
    const queryKey = chat_config_key()
    const {data: config} = useQuery<ChatConfig>({
        queryKey, queryFn: () => get_config(),
    })

    if (!config || !config?.host) return null

    const {host, user, password} = config

    const basic_auth = {
        'Authorization': 'Basic ' + btoa(`${user}:${password}`),
    }

/*
http://localhost:11434/api/generate -d '{
  "model": "llama3.2",
  "prompt": "Why is the sky blue?"
}'

curl http://localhost:11434/api/chat -d '{
  "model": "llama3.2",
  "messages": [
    {
      "role": "user",
      "content": "why is the sky blue?"
    }
  ]
}'

// PLAIN ANSWER

The message object has the following fields:

role: the role of the message, either system, user, assistant, or tool
content: the content of the message
images (optional): a list of images to include in the message (for multimodal models such as llava)
tool_calls (optional): a list of tools in JSON that the model wants to use

*/
  async function* getAnswer({
    question,
    chatItems,
    abortController,
  }: {
    question: string
    chatItems?: ChatItem[]
    abortController: AbortController
  }) {
    const messages = [
      ...(chatItems ?? []),
      {
        role: 'system',
        content: `You are an assistant for an IP management system. You are an expert for IP matters and for IP portfolios. Our IP rights (patents, trademarks etc.) are grouped into families.
Speak with certainty. Use (internal) references where possible. Try to keep your answers short and to the point. Do not invent anything.
NOTE: Avoid speaking about IP rights which are not in this portfolio (e.g. not in the search results).
`,
      },
      { role: 'user', content: question },
    ]
      .filter(item => item.role !== 'error')
      .filter(item => item.role !== 'tool') // do not include function calls. But steer the conversation by explicitly adding the needed context to the question
    try {
      const response = await fetch(`${host}/api/chat`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          ...basic_auth,
        },
        body: JSON.stringify({
          model: 'llama3.2',
          //prompt: question,
          messages,
        }),
      })

      const stream: ReadableStream = ndjsonStream(response.body)
      const reader = stream.getReader()

      while (true) {
        if (abortController.signal.aborted) {
          reader.cancel('aborted')
          break
        }
        const { done, value } = await reader.read()
        if (done) break
        yield value?.message?.content
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Aborted chat fetch')
        throw error
      } else {
        console.error('Error in chat fetch', error)
        throw error
      }
    }
  }

// You are an IP strategy specialist. Your job is to give clear, accurate, and concise answers. Stay on topic. Keep explanations short unless explicitly asked for details.
  async function analyzeQuestion({ question, chatItems, hasIpAsset }: { question: string; chatItems?: ChatItem[]; hasIpAsset: boolean }) {
    const messages = [
      ...(chatItems ?? []),
      {
        role: 'system',
        content: `You are an assistant for an IP management system. Decide, how to handle the question of the user.
Either use tool "answer" to answer the question or use another tool to add more context from the IP portfolio.
If there is a clear command for searching, use the "ip_search" tool.
If there is no explicit reference (something like P001, M002 etc.) use the "summarize" tool. 

NOTE: Avoid speaking about any kind of ID as they are form the database. Use the full references (internalReference or reference) instead.

User: what can you tell me about this patent?
Assistant: summarize

`,
//User: show me all the patents in the US
//Assistant: filter with ['US']
      },
      { role: 'user', content: question },
    ].filter(item => item.role !== 'error')
    const response = await fetch(`${host}/api/chat`, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        ...basic_auth,
      },
      body: JSON.stringify({
        model: 'llama3.2',
        messages,
        tools: tools.filter(t => hasIpAsset || t.function.name !== 'summarize'),
        stream: false,
        options: {
            temperature: 0.4,
        },
      }),
    })

    const data = await response.json()

    return data.message as ChatItem
  }

  const value = { analyzeQuestion, getAnswer, showTechnicalItems: config.showTechnicalItems }

  return <ChatContext.Provider {...{ value }}>{children}</ChatContext.Provider>
}
